<template>
  <!-- Mobile Backdrop Overlay (closes sidebar when clicked) -->
  <div 
    v-if="isOpen" 
    class="fixed inset-0 bg-black/50 z-40 md:hidden transition-opacity" 
    @click="$emit('update:isOpen', false)"
  ></div>

  <!-- Sidebar -->
  <aside 
    class="fixed inset-y-0 left-0 z-50 w-64 bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 transform transition-transform duration-300 ease-in-out md:translate-x-0"
    :class="isOpen ? 'translate-x-0' : '-translate-x-full'"
  >
    <!-- Header -->
  <div class="flex h-16 items-center px-6 border-b border-gray-200 dark:border-gray-700">
      <svg xmlns="http://www.w3.org/2000/svg" class="h-8 w-8 text-[#035a76]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
      </svg>
      <span class="ml-3 text-xl font-bold text-[#035a76] dark:text-white">School HR</span>
    </div>

    <!-- Navigation -->
    <nav class="overflow-y-auto h-[calc(100%-4rem)] scrollbar-thin scrollbar-thumb-gray-300 dark:scrollbar-thumb-gray-600">
<ul class="space-y-1 p-3">
        
        <!-- Dashboard -->
        <li>
          <RouterLink :to="{ name: 'hrdashboard' }" class="flex items-center px-4 py-3 rounded-lg hover:bg-[#74bd54] hover:text-white transition-colors duration-200 dark:hover:bg-gray-700" active-class="router-link-active">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
            </svg>
            Dashboard
          </RouterLink>
        </li>

        <!-- Checklist -->
        <li v-if="canAccessModule('checklist')">
          <RouterLink to="/checklist" class="flex items-center px-4 py-3 rounded-lg hover:bg-[#74bd54] hover:text-white transition-colors duration-200 dark:hover:bg-gray-700" active-class="router-link-active">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m1 8H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2z" />
            </svg>
            {{ $t('sidebar.checklist') }}
          </RouterLink>
        </li>

        <!-- Manpower -->
        <li v-if="canAccessModule('manpower')">
          <RouterLink to="/manpower" class="flex items-center px-4 py-3 rounded-lg hover:bg-[#74bd54] hover:text-white transition-colors duration-200 dark:hover:bg-gray-700" active-class="router-link-active">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-2a4 4 0 00-3-3.87M9 20h6m-3-4a4 4 0 100-8 4 4 0 000 8zm6-4a6 6 0 00-12 0v5h12v-5z" />
            </svg>
            {{ $t('sidebar.manpower') }}
          </RouterLink>
        </li>

        <!-- Users -->
        <li v-if="canAccessModule('users')">
          <RouterLink to="/hr-users" class="flex items-center px-4 py-3 rounded-lg hover:bg-[#74bd54] hover:text-white transition-colors duration-200 dark:hover:bg-gray-700" active-class="router-link-active">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
            </svg>
            {{ $t('sidebar.users') }}
          </RouterLink>
        </li>

        <!-- Audit Logs -->
        <li>
          <RouterLink to="/audit-logs" class="flex items-center px-4 py-3 rounded-lg hover:bg-[#74bd54] hover:text-white transition-colors duration-200 dark:hover:bg-gray-700" active-class="router-link-active">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            {{ $t('audits') }}
          </RouterLink>
        </li>

        <!-- Section Divider: Staff Management -->
        <li class="px-4 py-3 mt-4 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
          Staff Management
        </li>
        
        <!-- Onboarding -->
        <li>
          <RouterLink to="/onboarding" class="flex items-center px-4 py-3 rounded-lg hover:bg-[#74bd54] hover:text-white transition-colors duration-200 dark:hover:bg-gray-700" active-class="router-link-active">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
            </svg>
            {{ $t('sidebar.onboarding') }}
          </RouterLink>
        </li>
        
        <!-- Contracts -->
        <li v-if="canAccessModule('contracts')">
          <RouterLink to="/contracts" class="flex items-center px-4 py-3 rounded-lg hover:bg-[#74bd54] hover:text-white transition-colors duration-200 dark:hover:bg-gray-700" active-class="router-link-active">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            {{ $t('sidebar.contracts') }}
          </RouterLink>
        </li>
        
        <!-- Staff Directory -->
        <li>
          <RouterLink to="/staff" class="flex items-center px-4 py-3 rounded-lg hover:bg-[#74bd54] hover:text-white transition-colors duration-200 dark:hover:bg-gray-700" active-class="router-link-active">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
            {{ $t('sidebar.staffDirectory') }}
          </RouterLink>
        </li>
        
        <!-- Attendance -->
        <li>
          <RouterLink to="/attendance" class="flex items-center px-4 py-3 rounded-lg hover:bg-[#74bd54] hover:text-white transition-colors duration-200 dark:hover:bg-gray-700" active-class="router-link-active">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            {{ $t('sidebar.attendance') }}
          </RouterLink>
        </li>
        
        <!-- Leave Management -->
        <li>
          <RouterLink to="/leave" class="flex items-center px-4 py-3 rounded-lg hover:bg-[#74bd54] hover:text-white transition-colors duration-200 dark:hover:bg-gray-700" active-class="router-link-active">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            {{ $t('sidebar.leaveManagement') }}
          </RouterLink>
        </li>
        
        <!-- Section Divider: Payroll & Benefits -->
        <li class="px-4 py-3 mt-4 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
          Payroll & Benefits
        </li>
        
        <!-- Loans & Advances -->
        <li>
          <RouterLink to="/loans" class="flex items-center px-4 py-3 rounded-lg hover:bg-[#74bd54] hover:text-white transition-colors duration-200 dark:hover:bg-gray-700" active-class="router-link-active">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            {{ $t('sidebar.loansAdvance') }}
          </RouterLink>
        </li>
        
        <!-- Section Divider: Performance -->
        <li class="px-4 py-3 mt-4 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
          Performance
        </li>
        
        <!-- Appraisals -->
        <li>
          <RouterLink to="/appraisals" class="flex items-center px-4 py-3 rounded-lg hover:bg-[#74bd54] hover:text-white transition-colors duration-200 dark:hover:bg-gray-700" active-class="router-link-active">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
            {{ $t('sidebar.appraisals') }}
          </RouterLink>
        </li>

        <!-- Training & Development (External Link) -->
        <li>
          <a href="http://localhost/ecole_ronsard_elearning/admin/" target="_blank" rel="noopener noreferrer" class="flex items-center px-4 py-3 rounded-lg hover:bg-[#74bd54] hover:text-white transition-colors duration-200 dark:hover:bg-gray-700">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
            </svg>
            {{ $t('sidebar.trainingDevelopment') }}
            <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 ml-auto opacity-70" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
            </svg>
          </a>
        </li>
        
        <!-- Discipline -->
        <li>
          <RouterLink to="/discipline" class="flex items-center px-4 py-3 rounded-lg hover:bg-[#74bd54] hover:text-white transition-colors duration-200 dark:hover:bg-gray-700" active-class="router-link-active">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            Discipline
          </RouterLink>
        </li>
        
        <!-- Section Divider: Reports -->
        <li class="px-4 py-3 mt-4 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
          Reports
        </li>
        
        <!-- HR Reports -->
        <li>
          <RouterLink to="/reports" class="flex items-center px-4 py-3 rounded-lg hover:bg-[#74bd54] hover:text-white transition-colors duration-200 dark:hover:bg-gray-700" active-class="router-link-active">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            {{ $t('sidebar.hrReports') }}
          </RouterLink>
        </li>

        <!-- Section Divider: Master Data -->
        <li class="px-4 py-3 mt-4 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
          Master Data
        </li>
        
        <!-- Master Data Toggle Button -->
        <li>
          <button
            @click="toggleMasterData"
            :aria-expanded="showMasterData"
            class="flex items-center justify-between w-full px-4 py-3 rounded-lg hover:bg-[#74bd54] hover:text-white transition-colors duration-200 dark:hover:bg-gray-700 text-left"
          >
            <div class="flex items-center">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4m0 5c0 2.21-3.582 4-8 4s-8-1.79-8-4" />
              </svg>
              {{ $t('sidebar.masterData') }}
            </div>
            <svg
              :class="{ 'rotate-180': showMasterData }"
              class="h-4 w-4 transition-transform duration-200"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
            </svg>
          </button>

          <!-- Master Data Submenu -->
          <ul v-show="showMasterData" class="mt-1 space-y-1 bg-gray-50 dark:bg-gray-700/40 rounded-lg py-2">
            <li>
              <RouterLink to="/departments" class="flex items-center pl-12 pr-4 py-2 text-sm rounded-md hover:bg-[#74bd54] hover:text-white transition-colors duration-200" active-class="router-link-active">
                <svg class="h-4 w-4 mr-3 opacity-70" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5" /></svg>
                {{ $t('sidebar.departments') }}
              </RouterLink>
            </li>
            <li>
              <RouterLink to="/positions" class="flex items-center pl-12 pr-4 py-2 text-sm rounded-md hover:bg-[#74bd54] hover:text-white transition-colors duration-200" active-class="router-link-active">
                <svg class="h-4 w-4 mr-3 opacity-70" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>
                {{ $t('sidebar.positions') }}
              </RouterLink>
            </li>
            <li>
              <RouterLink to="/contract-types" class="flex items-center pl-12 pr-4 py-2 text-sm rounded-md hover:bg-[#74bd54] hover:text-white transition-colors duration-200" active-class="router-link-active">
                <svg class="h-4 w-4 mr-3 opacity-70" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                {{ $t('sidebar.contractTypes') }}
              </RouterLink>
            </li>
            <li>
              <RouterLink to="/process-types" class="flex items-center pl-12 pr-4 py-2 text-sm rounded-md hover:bg-[#74bd54] hover:text-white transition-colors duration-200" active-class="router-link-active">
                <svg class="h-4 w-4 mr-3 opacity-70" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>
                {{ $t('sidebar.processTypes') }}
              </RouterLink>
            </li>
            <li>
              <RouterLink to="/allowances" class="flex items-center pl-12 pr-4 py-2 text-sm rounded-md hover:bg-[#74bd54] hover:text-white transition-colors duration-200" active-class="router-link-active">
                <svg class="h-4 w-4 mr-3 opacity-70" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" /></svg>
                {{ $t('sidebar.allowances') }}
              </RouterLink>
            </li>
            <li>
              <RouterLink to="/contract-status" class="flex items-center pl-12 pr-4 py-2 text-sm rounded-md hover:bg-[#74bd54] hover:text-white transition-colors duration-200" active-class="router-link-active">
                <svg class="h-4 w-4 mr-3 opacity-70" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                {{ $t('sidebar.contractStatus') }}
              </RouterLink>
            </li>
            <li>
              <RouterLink to="/agency" class="flex items-center pl-12 pr-4 py-2 text-sm rounded-md hover:bg-[#74bd54] hover:text-white transition-colors duration-200" active-class="router-link-active">
                <svg class="h-4 w-4 mr-3 opacity-70" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
                {{ $t('sidebar.agencies') }}
              </RouterLink>
            </li>
            <li>
              <RouterLink to="/leave-types" class="flex items-center pl-12 pr-4 py-2 text-sm rounded-md hover:bg-[#74bd54] hover:text-white transition-colors duration-200" active-class="router-link-active">
                <svg class="h-4 w-4 mr-3 opacity-70" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                {{ $t('sidebar.leaveTypes') }}
              </RouterLink>
            </li>
            <li>
              <RouterLink to="/interview-questions" class="flex items-center pl-12 pr-4 py-2 text-sm rounded-md hover:bg-[#74bd54] hover:text-white transition-colors duration-200" active-class="router-link-active">
                <svg class="h-4 w-4 mr-3 opacity-70" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-4l-2 2v-2z" /></svg>
                {{ $t('sidebar.interviewQuestions') }}
              </RouterLink>
            </li>
            <li>
              <RouterLink to="/interview-evaluation" class="flex items-center pl-12 pr-4 py-2 text-sm rounded-md hover:bg-[#74bd54] hover:text-white transition-colors duration-200" active-class="router-link-active">
                <svg class="h-4 w-4 mr-3 opacity-70" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" /></svg>
                {{ $t('sidebar.interviewEvaluation') }}
              </RouterLink>
            </li>
            <li>
              <RouterLink to="/employee-actions" class="flex items-center pl-12 pr-4 py-2 text-sm rounded-md hover:bg-[#74bd54] hover:text-white transition-colors duration-200" active-class="router-link-active">
                <svg class="h-4 w-4 mr-3 opacity-70" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" /></svg>
                {{ $t('sidebar.employeeActions') }}
              </RouterLink>
            </li>
            <li>
              <RouterLink to="/attendance-status" class="flex items-center pl-12 pr-4 py-2 text-sm rounded-md hover:bg-[#74bd54] hover:text-white transition-colors duration-200" active-class="router-link-active">
                <svg class="h-4 w-4 mr-3 opacity-70" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                {{ $t('sidebar.attendanceStatus') }}
              </RouterLink>
            </li>
            <li>
              <RouterLink to="/working-hours" class="flex items-center pl-12 pr-4 py-2 text-sm rounded-md hover:bg-[#74bd54] hover:text-white transition-colors duration-200" active-class="router-link-active">
                <svg class="h-4 w-4 mr-3 opacity-70" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                {{ $t('sidebar.workingHours') }}
              </RouterLink>
            </li>
            <li>
              <RouterLink to="/sections" class="flex items-center pl-12 pr-4 py-2 text-sm rounded-md hover:bg-[#74bd54] hover:text-white transition-colors duration-200" active-class="router-link-active">
                <svg class="h-4 w-4 mr-3 opacity-70" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 10h16M4 14h16M4 18h16" /></svg>
                Sections
              </RouterLink>
            </li>
            <li>
              <RouterLink to="/classes" class="flex items-center pl-12 pr-4 py-2 text-sm rounded-md hover:bg-[#74bd54] hover:text-white transition-colors duration-200" active-class="router-link-active">
                <svg class="h-4 w-4 mr-3 opacity-70" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5" /></svg>
                Classes
              </RouterLink>
            </li>
            <li>
              <RouterLink to="/subjects" class="flex items-center pl-12 pr-4 py-2 text-sm rounded-md hover:bg-[#74bd54] hover:text-white transition-colors duration-200" active-class="router-link-active">
                <svg class="h-4 w-4 mr-3 opacity-70" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" /></svg>
                {{ $t('sidebar.subjects') }}
              </RouterLink>
            </li>
            <li v-if="canAccessModule('modules')">
              <RouterLink to="/modules" class="flex items-center pl-12 pr-4 py-2 text-sm rounded-md hover:bg-[#74bd54] hover:text-white transition-colors duration-200" active-class="router-link-active">
                <svg class="h-4 w-4 mr-3 opacity-70" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" /></svg>
                Modules
              </RouterLink>
            </li>
            <li v-if="canAccessModule('permissions')">
              <RouterLink to="/user-permission" class="flex items-center pl-12 pr-4 py-2 text-sm rounded-md hover:bg-[#74bd54] hover:text-white transition-colors duration-200" active-class="router-link-active">
                <svg class="h-4 w-4 mr-3 opacity-70" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" /></svg>
                {{ $t('sidebar.userPermission') }}
              </RouterLink>
            </li>
          </ul>
        </li>
      </ul>
    </nav>
  </aside>
</template>

<script setup>
import { ref } from 'vue';
import { usePermissions } from '@/composables/usePermissions';

const props = defineProps({
  isOpen: {
    type: Boolean,
    required: true
  }
});

const emit = defineEmits(['update:isOpen']);

const showMasterData = ref(false);
const { canAccessModule } = usePermissions();

const toggleMasterData = () => {
  showMasterData.value = !showMasterData.value;
};
</script>

<style scoped>
.router-link-active {
  background-color: #74bd54;
  color: white !important;
  font-weight: 600;
}
</style>